import { ENUM__error_type } from '@shared/asserts/ENUM__error_type';
import { assert_false, assert_is_safe_number, assert_number_gt_0, assert_number_gte_0, assert_true } from '@shared/asserts/utils_asserts';
import { DTO__base } from '@shared/common/DTO__base';
import { ENUM__dto_class_name } from '@shared/serialization/ENUM__dto_class_name';
import { DTO__image_size } from './DTO__image_size';

export class DTO__image_crop_box extends DTO__base {
  /*
   * STATIC FIELDS
   */
  static _class_name_static: string = ENUM__dto_class_name.IMAGE_CROP_BOX;

  static readonly ZERO = new DTO__image_crop_box().clone({ x: 0, y: 0, width: 0, height: 0 });

  /*
   * STATIC METHODS
   */

  static scaled_to_original(crop_box: DTO__image_crop_box, image_size_scaled: DTO__image_size, image_size_original: DTO__image_size) {
    assert_false(image_size_original.is_stub());
    assert_false(image_size_scaled.is_stub());

    const multiplier_x = image_size_original.width / image_size_scaled.width;
    const multiplier_y = image_size_original.height / image_size_scaled.height;

    return DTO__image_crop_box.multiply_crop_box(crop_box, multiplier_x, multiplier_y, image_size_original);
  }

  static original_to_scaled(crop_box: DTO__image_crop_box, image_size_scaled: DTO__image_size, image_size_original: DTO__image_size) {
    if (crop_box.is_stub()) return DTO__image_crop_box.CREATE_STUB();
    if (image_size_scaled.is_stub()) return DTO__image_crop_box.CREATE_STUB();
    if (image_size_original.is_stub()) return DTO__image_crop_box.CREATE_STUB();

    const multiplier_x = image_size_original.width / image_size_scaled.width;
    const multiplier_y = image_size_original.height / image_size_scaled.height;

    return DTO__image_crop_box.multiply_crop_box(crop_box, 1 / multiplier_x, 1 / multiplier_y, image_size_scaled);
  }

  static create_first_crop_box_for_image(image_size: DTO__image_size, target_aspect_ratio: number): DTO__image_crop_box {
    assert_false(image_size.is_stub());
    assert_number_gt_0(target_aspect_ratio);

    let out = new DTO__image_crop_box();

    const { width: canvas_width, height: canvas_height, aspect_ratio: canvas_aspect_ratio } = image_size;

    let [crop_width, crop_height] = [0, 0];

    // Determine the maximum crop_box size that fits inside the canvas image
    if (canvas_aspect_ratio > target_aspect_ratio) {
      // canvas image is wider than target aspect ratio
      crop_height = canvas_height;
      crop_width = Math.floor(crop_height * target_aspect_ratio);
    } else {
      // canvas image is taller than or equal to target aspect ratio
      crop_width = canvas_width;
      crop_height = Math.floor(crop_width / target_aspect_ratio);
    }

    [out.width, out.height] = [crop_width, crop_height];

    [out.x, out.y] = [(canvas_width - crop_width) / 2, (canvas_height - crop_height) / 2];

    out.floor_position();
    out.clamp_position();

    out.floor_size();
    out.clamp_size(image_size);

    return out;
  }

  static multiply_crop_box(crop_box: DTO__image_crop_box, multiplier_x: number, multiplier_y: number, image_size: DTO__image_size): DTO__image_crop_box {
    let out = new DTO__image_crop_box();

    [out.x, out.y] = [crop_box.x * multiplier_x, crop_box.y * multiplier_y];

    [out.width, out.height] = [crop_box.width * multiplier_x, crop_box.height * multiplier_y];

    out.floor_position();
    out.floor_size();
    out.clamp_size(image_size);

    return out;
  }

  static from_react_crop_object(crop_box: Record<string, any>) {
    let { x, y, width, height, unit } = crop_box;

    assert_is_safe_number(x);
    assert_is_safe_number(y);
    assert_is_safe_number(width);
    assert_is_safe_number(height);

    return new DTO__image_crop_box().clone({ x, y, width, height });
  }

  /*
   * INSTANCE FIELDS
   */

  x!: number;
  y!: number;

  width!: number;
  height!: number;

  /*
   * PROPERTIES
   */
  get size() {
    return new DTO__image_size(this.width, this.height);
  }

  /*
   * METHODS
   */

  constructor() {
    super();
  }

  floor_position() {
    assert_false(this.is_stub(), { assert_type: ENUM__error_type.VALIDATION_INVALID_VALUE });

    [this.x, this.y] = [Math.floor(this.x), Math.floor(this.y)];
  }

  floor_size() {
    assert_false(this.is_stub(), { assert_type: ENUM__error_type.VALIDATION_INVALID_VALUE });

    this.width = Math.floor(this.width);
    this.height = Math.floor(this.height);
  }

  clamp_position() {
    this.x = Math.max(0, this.x);
    this.y = Math.max(0, this.y);
  }

  clamp_size(size: DTO__image_size) {
    let difference_width = size.width - this.x - this.width;
    if (difference_width < 0) this.width += difference_width;

    let difference_height = size.height - this.y - this.height;
    if (difference_height < 0) this.height += difference_height;
  }

  is_point_inside(x: number, y: number): boolean {
    if (this.is_zero()) return false;

    return (
      x >= this.x &&
      x <= this.x + this.width && //
      y >= this.y &&
      y <= this.y + this.height
    );
  }

  is_equals(other: DTO__image_crop_box): boolean {
    return this.x == other.x && this.y == other.y && this.width == other.width && this.height == other.height;
  }

  is_zero(): boolean {
    return this.x == 0 && this.y == 0 && this.width == 0 && this.height == 0;
  }

  difference(other: DTO__image_crop_box) {
    let out = 0;

    out += Math.abs(this.x - other.x);
    out += Math.abs(this.y - other.y);
    out += Math.abs(this.width - other.width);
    out += Math.abs(this.height - other.height);

    return out;
  }

  is_cover_image_size(width: number, height: number) {
    if (this.x != 0) return false;
    if (this.y != 0) return false;
    if (this.width != width) return false;
    if (this.height != height) return false;

    return true;
  }

  toString(): string {
    return `${this.x}:${this.y}|${this.width}x${this.height}`;
  }

  to_css_object() {
    return {
      top: this.y,
      left: this.x,
      width: this.width,
      height: this.height,
    };
  }

  to_object(): Record<string, any> {
    return { x: this.x, y: this.y, width: this.width, height: this.height };
  }

  to_react_crop_object() {
    let out = {
      x: this.x,
      y: this.y,
      width: this.width,
      height: this.height,
      unit: 'px',
    };

    return out;
  }

  is_valid_for_image_size(image_size: DTO__image_size) {
    if (this.x + this.width > image_size.width) return false;
    if (this.y + this.height > image_size.height) return false;

    return true;
  }

  /*
   * ASSERTS
   */
  assert_standard_fields(): void {
    assert_number_gte_0(this.x);
    assert_number_gte_0(this.y);

    assert_number_gte_0(this.width);
    assert_number_gte_0(this.height);
  }

  assert_is_valid_for_image_size(image_size: DTO__image_size) {
    assert_false(this.is_stub());

    assert_number_gte_0(this.x);
    assert_number_gte_0(this.y);

    assert_true(this.is_valid_for_image_size(image_size));
  }
}
